import { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'hooks';
import { RootState } from 'store';
import { getAnalysisDataByCodeName } from 'api/home';
import { currencyCodeToSymbol } from 'pages/home/helpers/mapper';
import { setTodayBlockData } from 'actions/home';
import Icon from 'components/ui/icon';
import { Skeleton } from 'components/ui/skeleton';
import Button from 'components/ui/button';
import { OverallChip } from '../OverallChip/OverallChip';
import Utils from 'helpers/Utils';

interface Props {
  name: string;
  isDraggable?: boolean;
  isFetchDisabled?: boolean;
  isOverlay?: boolean;
  isDragDisabled?: boolean;
  isRedirectDisabled?: boolean;
}

export const SummaryCard: React.FC<Props> = ({
  name,
  isDraggable = false,
  isFetchDisabled = false,
  isOverlay = false,
  isDragDisabled = false,
  isRedirectDisabled = false,
}) => {
  const getTranslate = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const isFirstRender = useRef(true);
  const [isError, setError] = useState(false);
  const { state, currentCurrency, refreshId } = useSelector(
    (state: RootState) => ({
      state: state.home.todayBlockData[name],
      currentCurrency: state.home.currentCurrency,
      refreshId: state.home.refreshId,
    })
  );
  const { data, isLoaded = false } = state || {};
  const [isLoading, setLoading] = useState(!isLoaded);
  const {
    title = '',
    totalValue = '',
    text = '',
    percentageChange = '',
    tooltip = '',
    redirectPath = '',
  } = data || {};

  useEffect(() => {
    if (!isLoaded) {
      fetchData();
    }
  }, [isLoaded]);

  useEffect(() => {
    if (!isFirstRender.current) {
      fetchData();
    } else {
      isFirstRender.current = false;
    }
  }, [refreshId, currentCurrency]);

  const fetchData = async () => {
    if (isFetchDisabled) {
      return;
    }

    setLoading(true);
    setError(false);

    try {
      const { title, subTitle, value, increase, tooltip, redirectPath } =
        await getAnalysisDataByCodeName({
          blockCode: name,
          currency: currentCurrency,
        });

      dispatch(
        setTodayBlockData({
          name: name,
          data: {
            title,
            totalValue: value,
            text: subTitle,
            percentageChange: increase,
            tooltip,
            redirectPath,
          },
        })
      );
    } catch (err) {
      console.error(err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  if (isLoading || isError) {
    return (
      <div className='summary-card card max-tablet:h-[130px] max-tablet:p-4 flex h-[138px] w-full cursor-pointer flex-col justify-between overflow-hidden border border-transparent p-5'>
        <div className='summary-card__header flex justify-between gap-1'>
          <Skeleton height={14} width='100%' style={{ maxWidth: 100 }} />
          {isError ? (
            <Button
              id='refresh'
              status='light'
              icon='im-Refill'
              iconSize={14}
              iconPosition='left'
              text='Refresh'
              customClass='relative top-[-13px] right-[-10px] max-tablet:static'
              onClick={fetchData}
            />
          ) : null}
        </div>
        <div className='summary-card__body'>
          <div className='summary-card__values mb-2 flex items-center gap-2'>
            <Skeleton height={27} width={56} />
            <Skeleton height={27} width={32} />
          </div>
          <Skeleton height={17} width='100%' style={{ maxWidth: 117 }} />
        </div>
      </div>
    );
  }

  return (
    <div
      className={classnames(
        'summary-card card max-tablet:h-[130px] max-tablet:p-4 flex h-[138px] w-full cursor-pointer flex-col justify-between overflow-hidden border border-transparent p-5',
        {
          'summary-card--draggable hover:border-brand-main cursor-grab touch-none hover:border':
            isDraggable && !isDragDisabled,
          'summary-card--drag-disabled cursor-not-allowed': isDragDisabled,
          'summary-card--overlay cursor-grabbing shadow-[0px_10px_10px_0px_rgba(0,0,0,0.2)]':
            isOverlay,
        }
      )}
      onClick={() => {
        if (!isRedirectDisabled) {
          history.push(redirectPath);
        }
      }}>
      <div className='summary-card__header flex gap-2'>
        <h5 className='summary-card__title text-m-medium'>{getTranslate(title)}</h5>
        {!isDraggable && tooltip ? (
          <Icon
            tooltip={getTranslate(tooltip)}
            size={16}
            name='im-Info'
            className='summary-card__tooltip-icon text-text-tertiary max-tablet:hidden'
          />
        ) : null}
      </div>
      <div className='summary-card__body'>
        <div className='summary-card__values mb-2 flex items-center gap-2'>
          <h3 className='summary-card__total font-heading text-heading-m whitespace-nowrap'>
            {name !== 'purchases_total' &&
              currencyCodeToSymbol[currentCurrency]}{' '}
            {Utils.getNumberWithSuffix(totalValue)}
          </h3>

          {percentageChange ? (
            <div className='summary-card__overall-chip max-desktop-s:hidden'>
              <OverallChip value={percentageChange} />
            </div>
          ) : null}

          {isDraggable ? (
            <Icon
              name='draggable'
              size={16}
              className='summary-card__drag-icon absolute right-4 bottom-4 scale-110'
            />
          ) : null}
        </div>
        <p className='summary-card__subtitle text-text-secondary text-sm-regular whitespace-nowrap'>
          {text}
        </p>
      </div>
    </div>
  );
};
